import React, { useEffect, useState } from "react";
import { Typography, Tooltip, Button } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import theme, { buttonClasses, textClasses } from "../../../static/styles/theme";
import axios from "../../../services/Api";
import GenericAddImageModal from "./GenericAddImageModal";

const IMAGE_REGEX = /\.(jpg|jpeg|png|gif|bmp|tiff)$/i;

export function useFileThumbnail(fileKey) {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [thumbnailLoading, setThumbnailLoading] = useState(false);

    useEffect(() => {
        if (!fileKey || !IMAGE_REGEX.test(fileKey)) {
            setThumbnailUrl(null);
            return;
        }
        let cancelled = false;
        setThumbnailLoading(true);
        axios
            .post(`/inventory/getS3FileForItem`, { fileKey, thumbnail: true })
            .then(({ data: res }) => {
                if (!cancelled) setThumbnailUrl(res.fileUrl);
            })
            .catch(() => {
                if (!cancelled) setThumbnailUrl(null);
            })
            .finally(() => {
                if (!cancelled) setThumbnailLoading(false);
            });
        return () => { cancelled = true; };
    }, [fileKey]);

    return { thumbnailUrl, thumbnailLoading };
}

/**
 * GenericFileThumbnail
 *
 * No fileKey + documentId/fieldId provided → "Add Image" button + GenericAddImageModal
 * No fileKey + no context props          → "No Img"
 *
 * variant="detail" (default) — fetches and shows thumbnail inline
 * variant="table"            — "View" text + eye icon + tooltip; clicking calls onClick(fileKey)
 */
export default function GenericFileThumbnail({
    fileKey,
    onClick,
    showThumbnail = true,
    variant = "detail",
    moduleName = "",
    documentId = "",
    fieldId = "",
    onSuccess,
}) {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const { thumbnailUrl, thumbnailLoading } = useFileThumbnail(
        variant === "detail" && showThumbnail ? fileKey : null
    );

    // ── No file ────────────────────────────────────────────────────────────────
    if (!fileKey) {
        if (documentId && fieldId) {
            return (
                <>
                    <Button
                        size="small"
                        variant="contained"
                        sx={{ ...buttonClasses.lynkitOrangeEmpty, height: "24px", fontSize: "11px", minWidth: "80px" }}
                        onClick={() => setAddModalOpen(true)}
                    >
                        Add Image
                    </Button>
                    <GenericAddImageModal
                        open={addModalOpen}
                        onClose={() => setAddModalOpen(false)}
                        moduleName={moduleName}
                        documentId={documentId}
                        fieldId={fieldId}
                        onSuccess={onSuccess}
                    />
                </>
            );
        }
       return <Typography sx={{ ...textClasses.t12n }}>No Img</Typography>;
    }

    // ── Table variant ──────────────────────────────────────────────────────────
    if (variant === "table") {
        return (
            <Tooltip title="View Image" placement="top">
                <div
                    onClick={() => onClick?.(fileKey)}
                    style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
                >
                    <Typography sx={{ ...textClasses.t12n, color: theme.themeOrange }}>View</Typography>
                    <VisibilityIcon sx={{ fontSize: "14px", color: theme.themeOrange }} />
                </div>
            </Tooltip>
        );
    }

    // ── Detail variant (default) ───────────────────────────────────────────────
    const isImage = IMAGE_REGEX.test(fileKey);

    if (thumbnailLoading) {
        return <Typography sx={{ ...textClasses.t12n }}>Loading...</Typography>;
    }

    if (showThumbnail && isImage && thumbnailUrl) {
        return (
            <img
                src={thumbnailUrl}
                alt="thumbnail"
                style={{
                    width: 60,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: `1px solid ${theme.themeOrange}`,
                    cursor: "pointer",
                }}
                onClick={() => onClick?.(fileKey)}
            />
        );
    }

    return (
        <div onClick={() => onClick?.(fileKey)} style={{ cursor: "pointer" }}>
            <span style={{ ...buttonClasses.viewFile, cursor: "pointer" }}>View File</span>
        </div>
    );
}
